import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { APPINSIGHTS_STRINGS } from "../../constants/appInsightsStrings";
import { trackEvent } from "../../utils/appInsights";

import {
  savePlant,
  editPlant,
  getPlantDetails,
  getWaterQualityDefaults,
} from "../../network/cipAPI";
import { storeUnits } from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import ErrorLabel from "../../components/ErrorLabel";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DupontButton from "../../components/DupontButton";
import PageLoading from "../../components/PageLoading";
import DupontToggle from "../../components/DupontToggle";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import Settings from "../home/Settings";

import {
  getChangedData,
  extractPlantId,
  updatePlantId,
} from "../../utils/Utils";
import { saveItemSession } from "../../utils/session";
import {
  PLANT_DETAILS,
  WATER_TYPE,
  FEED_WATER_LIMIT_FIELDS,
} from "./addNewPlant.config";
import RoConfiguration from "./roConfiguration";
import { API_URLS } from "../../network/appUrls";
import "./index.scss";
import Footer from "../../common/footer";
import { MODULE_BRAND_KEYS } from "../../constants/globalStrings";

const basePath = API_URLS.basePath;
const FEED_WATER_LIMITS_INITIAL_STATE = FEED_WATER_LIMIT_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field.key]: "" }),
  {}
);
const FEED_WATER_LIMITS_RANGE_MAP = FEED_WATER_LIMIT_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field.key]: { min: field.min, max: field.max } }),
  {}
);


const AddNewPlant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plantDetails = PLANT_DETAILS(t);
  let countryOptions = useMemo(() => countryList().getData(), []);
  countryOptions = countryOptions.map((item) => item.label);
  const waterTypeOptions = useMemo(() => WATER_TYPE(t), [t]);

  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.get("isEdit");

  const [plantDetail, setPlantDetail] = useState({});
  const [plantDetailerror, setPlantDetailError] = useState({});
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [totalNumberOfActiveArea, setTotalNumberOfActiveArea] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [moduleBrand, setModuleBrand] = useState(MODULE_BRAND_KEYS[0]);
  const [isFeedWaterLimitsEnabled, setIsFeedWaterLimitsEnabled] =
    useState(false);
  const [feedWaterLimits, setFeedWaterLimits] = useState(
    FEED_WATER_LIMITS_INITIAL_STATE
  );
  const {
    flow,
    pressure,
    feed_suspended,
    filtrate_suspended,
    temperature,
    flux,
    volume,
    mass,
    currency,
    conversion_rate,
    userEmail,
    token,
  } = useSelector((state) => state);
  const plantId = extractPlantId();

  const moduleBrandOptions = MODULE_BRAND_KEYS.map((key) =>
    t(`addNewPlant.${key}`)
  );
  // Get key from label (for dropdown selection)
  const getModuleBrandKey = (label) =>
    MODULE_BRAND_KEYS.find((key) => t(`addNewPlant.${key}`) === label);
  // Get label from key (for display)
  const getModuleBrandLabel = (key) => {
    if (!key) return "";
    return t(`addNewPlant.${key}`);
  };

  useEffect(() => {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          const response = await getPlantDetails();
          if (response && response.data) {
            const detail = response.data;
            const {
              name,
              description,
              city,
              country,
              water_type,
              module_brand,
              config,
              setting,
            } = detail;

            setFetchedPlanDetails(detail);
            setPlantDetail({
              ...plantDetail,
              installationName: name,
              description,
              city,
            });
            setCountry(country);
            setWaterType(water_type);
            if (module_brand) {
              setModuleBrand(module_brand);
            }
            setIsFeedWaterLimitsEnabled(Boolean(detail?.water_quality_limits_enabled));
            setFeedWaterLimits({
              turbidity_limit: String(detail?.turbidity_limit ?? ""),
              tss_limit: String(detail?.tss_limit ?? ""),
              toc_limit: String(detail?.toc_limit ?? ""),
            });
            setTrains(config?.trains);
            dispatch(storeUnits(setting));
          }
        } catch (error) {
          console.log("getPlantDetail", error);
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, ["country"]: "" });
  };
  const updateFeedWaterLimitsFromDefaults = async (selectedWaterType) => {
    if (!selectedWaterType) return;

    try {
      const response = await getWaterQualityDefaults(selectedWaterType);
      const defaults = response?.data || {};

      const updatedLimits = {
        turbidity_limit: String(defaults.turbidity_limit ?? ""),
        tss_limit: String(defaults.tss_limit ?? ""),
        toc_limit: String(defaults.toc_limit ?? ""),
      };

      setFeedWaterLimits((prev) => ({ ...prev, ...updatedLimits }));
      setPlantDetailError((prev) => ({
        ...prev,
        turbidity_limit: "",
        tss_limit: "",
        toc_limit: "",
      }));
    } catch (error) {
      console.log("getWaterQualityDefaults", error);
    }
  };

  const changeWaterType = async (value) => {
    setWaterType(value);
    setPlantDetailError((prev) => ({ ...prev, ["waterType"]: "" }));
    await updateFeedWaterLimitsFromDefaults(value);
  };
  const changeModuleBrand = (value) => {
    setModuleBrand(getModuleBrandKey(value));
    setPlantDetailError({ ...plantDetailerror, ["moduleBrand"]: "" });
  };
  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    setPlantDetailError({ ...plantDetailerror, [key]: "" });
  };
  const onFeedWaterLimitChanged = (key, value) => {
    setFeedWaterLimits((prev) => ({ ...prev, [key]: value }));
    if (!isFeedWaterLimitsEnabled) {
      setPlantDetailError((prev) => ({ ...prev, [key]: "" }));
      return;
    }

    const fieldConfig = FEED_WATER_LIMIT_FIELDS.find((field) => field.key === key);
    if (!fieldConfig) {
      setPlantDetailError((prev) => ({ ...prev, [key]: "" }));
      return;
    }

    const isValid = isWithinRange(key, value);
    setPlantDetailError((prev) => ({
      ...prev,
      [key]: isValid ? "" : fieldConfig.errorKey,
    }));
  };
  const isWithinRange = (key, value) => {
    const range = FEED_WATER_LIMITS_RANGE_MAP[key];
    if (!range || value === "" || value === undefined || value === null) {
      return false;
    }
    const numericValue = parseFloat(value);
    return (
      !isNaN(numericValue) &&
      numericValue >= range.min &&
      numericValue <= range.max
    );
  };
  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    const error = {};
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      error["installationName"] = "addNewPlant.installationNameRequired";
    }
    if (!country) {
      validationSuccessful = false;
      error["country"] = "addNewPlant.countryRequired";
    }
    if (!waterType) {
      validationSuccessful = false;
      error["waterType"] = "addNewPlant.waterTypeRequired";
    }
    if (!moduleBrand) {
      validationSuccessful = false;
      error["moduleBrand"] = "addNewPlant.moduleBrandRequired";
    }
    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    for (const train of Object.values(trains)) {
      if (train?.racks?.length === 0) {
        validationSuccessful = false;
        break;
      }

      for (const rack of train.racks) {
        if (
          rack.num_modules_per_rack === undefined ||
          // rack.modules_used === undefined ||
          rack.active_area === undefined
        ) {
          validationSuccessful = false;
          break;
        }
      }
    }
    return validationSuccessful;
  };
  const normalizedFetchedRate = parseFloat(
    fetchedPlanDetails?.setting?.conversion_rate ?? ""
  );
  const normalizedCurrentRate = parseFloat(conversion_rate ?? "");
  const isConversionRateChanged =
    normalizedFetchedRate !== normalizedCurrentRate;

  const toNumberOrNull = (value) =>
    value === "" || value === undefined || value === null
      ? null
      : Number(value);

  const savePlantDetails = async (key) => {
    setIsLoading(true);
    try {
      const validated = runValidation();
      let response = null;
      if (validated) {
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          water_quality_limits_enabled: isFeedWaterLimitsEnabled,
          turbidity_limit: toNumberOrNull(feedWaterLimits.turbidity_limit),
          tss_limit: toNumberOrNull(feedWaterLimits.tss_limit),
          toc_limit: toNumberOrNull(feedWaterLimits.toc_limit),
          module_brand: moduleBrand,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_suspended: feed_suspended,
            filtrate_suspended: filtrate_suspended,
            temperature: temperature,
            flux: flux,
            volume: volume,
            mass: mass,
            currency: currency,
            ...(isConversionRateChanged && {
              conversion_rate: conversion_rate,
            }),
          },
          config: {
            trains: [...Object.values(trains)],
          },
          tol_num_modules: totalNumberOfElements,
          tol_active_area: totalNumberOfActiveArea,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("name", plantDetail.installationName);
        if (plantId && isEdit) {
          const editedFields = getChangedData(payload, fetchedPlanDetails);
          response = await editPlant(editedFields);
        } else {
          response = await savePlant(payload);
        }
        if (response.created_at) {
          // Track successful plant creation/edit
          trackEvent(
            isEdit
              ? APPINSIGHTS_STRINGS.PLANT_EDITED
              : APPINSIGHTS_STRINGS.PLANT_ADDED,
            {
              userEmail: userEmail,
              plantName: plantDetail.installationName,
              country: country,
              waterType: waterType,
              totalElements: totalNumberOfElements,
              s: totalNumberOfActiveArea,
            }
          );

          if (key === "exit") {
            navigate(-1);
            const message = isEdit
              ? t("addNewPlant.successfullyEdited")
              : t("addNewPlant.successfullyAdded");
            createNotification(SUCCESS_NOTIFICATION, message, "");
          } else {
            updatePlantId(response.plant_id);
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${response.plant_id}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${response.plant_id}`;
            navigate(path);
          }
        } else {
          createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.log("savePlantDetails", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const hasFeedWaterLimitErrors =
    isFeedWaterLimitsEnabled &&
    FEED_WATER_LIMIT_FIELDS.some((field) => Boolean(plantDetailerror[field.key]));

  return (
    <div className="container-main">
      <Header />
      <div className="main-container">
        <div className="content-container descriptionText">
          {isLoading ? (
            <PageLoading customHeight={"515px"} />
          ) : (
            <div className="calculatedValueText plant-setup">
              <div className="plant-setup-title">
                {isEdit
                  ? t("roAdvisorProject.editPlant")
                  : t("roAdvisorProject.addPlant")}
              </div>
              <section className="ro-card-wrapper plant-setup-card">
                <div className="ro-card-header">
                  <div className="teal-heading">
                    {t("addNewPlant.plantInformation")}
                  </div>
                </div>
                <div className="plant-information-grid">
                  {[plantDetails.installationName, plantDetails.city].map(
                    (field) => (
                      <div key={field.key}>
                        <InputBox
                          name={field.key}
                          value={plantDetail[field.key]}
                          labelText={t(`addNewPlant.${field.key}`)}
                          placeholder={t(`addNewPlant.${field.key}`)}
                          rows={field.multiline}
                          isRequired={field.required}
                          onChange={(value) =>
                            handleInputChange(field.key, value)
                          }
                          errorMsg={
                            plantDetailerror[field.key]
                              ? t(plantDetailerror[field.key])
                              : ""
                          }
                          maxLength={field.maxChar}
                        />
                      </div>
                    ),
                  )}
                  <div>
                    <Dropdown
                      labelText={t("addNewPlant.country")}
                      placeholder={t("addNewPlant.country")}
                      value={country}
                      onChange={changeCountry}
                      options={countryOptions}
                      errorMsg={
                        plantDetailerror["country"]
                          ? t(plantDetailerror["country"])
                          : ""
                      }
                      isRequired
                    />
                  </div>
                </div>

                <div className="plant-water-grid">
                  <div className="plant-water-type">
                    <Dropdown
                      labelText={t("addNewPlant.waterType")}
                      placeholder={t("addNewPlant.waterType")}
                      value={waterType}
                      onChange={changeWaterType}
                      options={waterTypeOptions}
                      errorMsg={
                        plantDetailerror["waterType"]
                          ? t(plantDetailerror["waterType"])
                          : ""
                      }
                      isRequired
                      hideClearBtn
                    />
                  </div>
                  <div className="plant-module-brand">
                    <Dropdown
                      labelText={t("addNewPlant.moduleBrand")}
                      placeholder={t("addNewPlant.moduleBrand")}
                      value={getModuleBrandLabel(moduleBrand)}
                      onChange={changeModuleBrand}
                      options={moduleBrandOptions}
                      isRequired
                      errorMsg={
                        plantDetailerror["moduleBrand"]
                          ? t(plantDetailerror["moduleBrand"])
                          : ""
                      }
                    />
                  </div>
                </div>
                <InputBox
                  name="description"
                  value={plantDetail.description}
                  labelText={t("addNewPlant.description")}
                  placeholder={t("addNewPlant.description")}
                  rows={plantDetails.description.multiline}
                  maxLength={plantDetails.description.maxChar}
                  onChange={(value) => handleInputChange("description", value)}
                  errorMsg={
                    plantDetailerror.description
                      ? t(plantDetailerror.description)
                      : ""
                  }
                />
              </section>
              <section className="ro-card-wrapper plant-setup-card">
                <div className="ro-card-header feed-water-limits-header">
                  <div className="teal-heading">
                    {t("addNewPlant.feedWaterLimits")}
                  </div>
                  <div className="feed-water-limits-toggle">
                    <DupontToggle
                      id="feed-water-limits-toggle"
                      name="feed-water-limits-toggle"
                      checked={isFeedWaterLimitsEnabled}
                      onChange={(isEnabled) => {
                        setIsFeedWaterLimitsEnabled(isEnabled);
                        if (!isEnabled) {
                          const clearedLimitErrors =
                            FEED_WATER_LIMIT_FIELDS.reduce(
                              (acc, field) => ({ ...acc, [field.key]: "" }),
                              {},
                            );
                          setPlantDetailError({
                            ...plantDetailerror,
                            ...clearedLimitErrors,
                          });
                        }
                      }}
                      optionLabels={["ON", "OFF"]}
                      small
                    />
                  </div>
                </div>
                <div className="plant-limits-grid">
                  {FEED_WATER_LIMIT_FIELDS.map((field) => (
                    <div className="flex_1" key={field.key}>
                      <InputBox
                        name={field.key}
                        value={feedWaterLimits[field.key]}
                        labelText={t(field.labelKey)}
                        placeholder={t(field.labelKey)}
                        onChange={(value) =>
                          onFeedWaterLimitChanged(field.key, value)
                        }
                        hideClearBtn
                        errorMsg={
                          plantDetailerror[field.key]
                            ? t(plantDetailerror[field.key])
                            : ""
                        }
                        validator={TextFieldValidator.NUMBER_ONLY}
                        isRequired={isFeedWaterLimitsEnabled}
                        disabled={!isFeedWaterLimitsEnabled}
                        maxLength={field.maxLength}
                      />
                    </div>
                  ))}
                </div>
              </section>
              <section className="ro-card-wrapper plant-setup-card">
                <div className="ro-card-header">
                  <div className="teal-heading flex-ro">
                    {t("addNewPlant.reverseOsmosis")}
                    {!stageValidationSucessfull && (
                      <ErrorLabel
                        className="margin-left-ro"
                        message={t("addNewPlant.mandatoryRequired")}
                      />
                    )}
                  </div>
                </div>
                <div className="reverse-osmosis description-text">
                  <RoConfiguration
                    trains={trains}
                    setTrains={setTrains}
                    setTotalNumberOfElements={setTotalNumberOfElements}
                    setTotalNumberOfActiveArea={setTotalNumberOfActiveArea}
                    rackValidationSucessfull={stageValidationSucessfull}
                    setStageValidationSucessfull={setStageValidationSucessfull}
                  />

                  <div className="flex-between padding-top-20">
                    <div className="flex-between-center">
                      {t("addNewPlant.totalNoOfActiveArea")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfActiveArea}
                          placeholder={t(`addNewPlant.totalNoOfActiveArea`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                    <div className="flex-between-center">
                      {t("addNewPlant.totalNoOfElements")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfElements}
                          placeholder={t(`addNewPlant.totalNoOfElements`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <Settings
                className="ro-card-wrapper plant-setup-card plant-settings-card"
                disabled={false}
                descriptionText={t("addNewPlant.unitOfMeasureDescription")}
              />
              <div className="flex-jc-end padding-top-20 ">
                <DupontButton
                  customClass={"margin-bottom margin-left-10"}
                  onClick={() => savePlantDetails("exit")}
                  disabled={!stageValidationSucessfull || hasFeedWaterLimitErrors}
                >
                  {t("ValueStrings.saveExit")}
                </DupontButton>
                <DupontButton
                  customClass={"margin-bottom margin-left-10"}
                  onClick={() => savePlantDetails("continue")}
                  disabled={!stageValidationSucessfull || hasFeedWaterLimitErrors}
                >
                  {t("ValueStrings.saveContinue")}
                </DupontButton>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default AddNewPlant;
